import React from "react";
import "./BracketBoard.css";

export interface BracketViewEntrant {
  id: string;
  name: string;
  clubName?: string | null;
  seedRank?: number | null;
}

export interface BracketViewMatch {
  id: string;
  roundIndex: number;
  matchIndex: number;
  fightNumber: number | null;
  redEntrantId: string | null;
  blueEntrantId: string | null;
  winnerEntrantId: string | null;
  loserEntrantId?: string | null;
  resultType?: string | null;
  isBye: boolean;
}

export interface BracketViewDraw {
  rounds: BracketViewMatch[][];
  entrants: Record<string, BracketViewEntrant>;
}

interface BracketLabels {
  round: string;
  semifinal: string;
  final: string;
  fight: string;
  bye: string;
  waiting: string;
  seed: string;
  winner: string;
  undo: string;
}

interface Props {
  draw: BracketViewDraw;
  labels: BracketLabels;
  interactive?: boolean;
  highlightEntrantId?: string | null;
  onSelectWinner?: (match: BracketViewMatch, entrantId: string) => void;
  onUndoResult?: (match: BracketViewMatch) => void;
}

function roundLabel(index: number, count: number, labels: BracketLabels): string {
  if (index === count - 1) return labels.final;
  if (index === count - 2) return labels.semifinal;
  return `${labels.round} ${index + 1}`;
}

function resultBadge(type: string | null | undefined): string | null {
  if (!type || type === "standard" || type === "bye") return null;
  return type.replace(/_/g, " ").toUpperCase();
}

export default function BracketBoard({
  draw,
  labels,
  interactive = false,
  highlightEntrantId,
  onSelectWinner,
  onUndoResult,
}: Props) {
  return (
    <div className="tournament-bracket" role="region" aria-label="Tournament bracket">
      {draw.rounds.map((round, roundIndex) => (
        <section className="tournament-bracket-round" key={roundIndex}>
          <h3>{roundLabel(roundIndex, draw.rounds.length, labels)}</h3>
          <div className="tournament-bracket-round-matches">
            {round.map((match) => {
              const active = !!match.redEntrantId && !!match.blueEntrantId && !match.isBye;
              const badge = resultBadge(match.resultType);
              return (
                <article className={`tournament-bracket-match ${active ? "is-active" : ""}`} key={match.id}>
                  <header>
                    <span>{match.fightNumber ? `${labels.fight} ${match.fightNumber}` : labels.bye}</span>
                    <span>{badge ?? (match.winnerEntrantId ? labels.winner : "")}</span>
                  </header>
                  {([match.redEntrantId, match.blueEntrantId] as const).map((entrantId, side) => {
                    const entrant = entrantId ? draw.entrants[entrantId] : null;
                    const isWinner = !!entrantId && entrantId === match.winnerEntrantId;
                    const isLoser = !!entrantId && entrantId === match.loserEntrantId;
                    const isAdvanced = !!entrantId && entrantId === highlightEntrantId;
                    return (
                      <button
                        type="button"
                        key={`${match.id}-${side}`}
                        className={[
                          "tournament-bracket-competitor",
                          side === 0 ? "side-red" : "side-blue",
                          isWinner ? "is-winner" : "",
                          isLoser ? "is-loser" : "",
                          isAdvanced ? "is-advanced" : "",
                        ].filter(Boolean).join(" ")}
                        disabled={!interactive || !active || !entrantId}
                        onClick={() => entrantId && onSelectWinner?.(match, entrantId)}
                      >
                        <span className="tournament-bracket-side" />
                        <span className="tournament-bracket-person">
                          <strong>{entrant?.name ?? (match.isBye ? labels.bye : labels.waiting)}</strong>
                          <small>{entrant?.clubName || "—"}</small>
                        </span>
                        {entrant?.seedRank != null && <em>{labels.seed} {entrant.seedRank}</em>}
                        {isWinner && <span className="tournament-bracket-outcome winner">✓</span>}
                        {isLoser && <span className="tournament-bracket-outcome loser">×</span>}
                      </button>
                    );
                  })}
                  {interactive && match.winnerEntrantId && !match.isBye && (
                    <button
                      type="button"
                      className="tournament-bracket-undo"
                      onClick={() => onUndoResult?.(match)}
                    >
                      {labels.undo}
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
